import type { CSSProperties, ReactNode } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  AlertCircleIcon, AtIcon, Add01Icon, Archive01Icon, ArrowRight01Icon, ArrowRight02Icon, ArrowUpRight01Icon,
  BubbleChatIcon, Calendar03Icon, Camera01Icon, Cancel01Icon,
  CheckmarkCircle01Icon, Copy01Icon, Delete02Icon, Download01Icon,
  FloppyDiskIcon, Image01Icon, InformationCircleIcon, Link01Icon,
  Login01Icon, Logout01Icon, Menu01Icon, Refresh01Icon, Share01Icon, Tick02Icon,
  Unlink01Icon, Upload01Icon, UserAdd01Icon, PencilEdit01Icon, MoreHorizontalIcon, SentIcon, Mail01Icon, Rocket01Icon,
} from '@hugeicons/core-free-icons';

/** The shared icon map: a modal action or title names its glyph by kind. */
export const MODAL_ICONS = {
  mention: AtIcon,
  // An error dialog's title (Dialog.tsx): the circle with a mark, the glyph the review page wall always drew.
  alert: AlertCircleIcon,
  edit: PencilEdit01Icon, more: MoreHorizontalIcon, send: SentIcon, email: Mail01Icon,
  add: Add01Icon, archive: Archive01Icon, next: ArrowRight01Icon,
  // The arrow with a shaft, for a step that moves on; it trails its label (placement="trailing").
  forward: ArrowRight02Icon,
  open: ArrowUpRight01Icon, help: BubbleChatIcon, schedule: Calendar03Icon,
  capture: Camera01Icon, cancel: Cancel01Icon, verify: CheckmarkCircle01Icon,
  copy: Copy01Icon, delete: Delete02Icon, export: Download01Icon,
  save: FloppyDiskIcon, image: Image01Icon, info: InformationCircleIcon,
  link: Link01Icon, login: Login01Icon, logout: Logout01Icon, menu: Menu01Icon,
  retry: Refresh01Icon, share: Share01Icon, done: Tick02Icon,
  unlink: Unlink01Icon, upload: Upload01Icon, invite: UserAdd01Icon,
  // Starting a free trial (Emily, 2026-10-22: "start free trial show rocket icon").
  trial: Rocket01Icon,
} as const;

export type ModalIconKind = keyof typeof MODAL_ICONS;

/** Decorative glyph: the adjacent title or action label supplies the name. */
export function ModalIcon({ kind }: { kind: ModalIconKind }) {
  return <HugeiconsIcon icon={MODAL_ICONS[kind]} size={16} strokeWidth={2}
    color="currentColor" aria-hidden="true" data-modal-icon={kind}
    style={{ display: 'inline-block', flexShrink: 0, verticalAlign: 'middle' }} />;
}

/** Keep glyph placement and caller spacing together across host surfaces. */
export function ModalAction({ kind, placement = 'leading', children, style }: {
  kind: ModalIconKind; placement?: 'leading' | 'trailing'; children: ReactNode; style?: CSSProperties;
}) {
  const glyph = <ModalIcon kind={kind} />;
  return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--btn-gap, var(--oq-space-1h, 6px))', ...style }}>
    {placement === 'leading' && glyph}{children}{placement === 'trailing' && glyph}
  </span>;
}
